import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePilgrimGuideStore } from '../../stores/pilgrimGuideStore';
import { ErrorMessage } from '../common/ErrorMessage';
import { useLanguage } from '../../contexts/LanguageContext';
import { Loader2, ChevronRight, ChevronLeft, Search, Filter, User, Building2, Building } from 'lucide-react';
import { LoadingProgress } from '../common/LoadingProgress';
import { LinkingTableRow } from './LinkingTableRow';

const ITEMS_PER_PAGE = 50;

export const LinkingTable = () => {
  const { 
    links, 
    error, 
    initialized,
    loading,
    hasMore,
    progress,
    fetchLinks,
    loadMore,
  } = usePilgrimGuideStore();
  const { translations } = useLanguage();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedOrganizer, setSelectedOrganizer] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const statusLabels: Record<string, string> = {
    approved: translations?.linkingTable?.filters?.approved || 'Approved',
    pending: translations?.linkingTable?.filters?.pending || 'Pending',
    disputed: translations?.linkingTable?.filters?.disputed || 'Disputed',
    rejected: translations?.linkingTable?.filters?.rejected || 'Rejected',
    waiting_identification:
      translations?.linkingTable?.filters?.waitingIdentification || 'Waiting Identification',
  };

  // Initialize data
  useEffect(() => {
    if (!initialized) {
      fetchLinks();
    }
  }, [initialized, fetchLinks]);

  // Get unique organizers
  const organizers = useMemo(() => {
    return Array.from(new Set(Object.values(links).map(link => link.organizerName))).sort();
  }, [links]);

  // Get unique countries
  const countries = useMemo(() => {
    return Array.from(new Set(Object.values(links).map(link => link.residenceCountry))).sort();
  }, [links]);

  // Memoized filtered links
  const filteredLinks = useMemo(() => {
    return Object.values(links).filter(link => {
      const matchesSearch = !searchTerm || 
        link.pilgrimName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(link.passportNumber).toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = !selectedStatus || (
        selectedStatus === 'disputed' ? link.disputeId !== undefined :
        link.status === selectedStatus
      );
      
      const matchesCountry = !selectedCountry || link.residenceCountry === selectedCountry;
      const matchesOrganizer = !selectedOrganizer || link.organizerName === selectedOrganizer;

      return matchesSearch && matchesStatus && matchesCountry && matchesOrganizer;
    }); 
  }, [links, searchTerm, selectedStatus, selectedCountry, selectedOrganizer]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredLinks.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedLinks = filteredLinks.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  if (error) {
    return <ErrorMessage message={error} onRetry={fetchLinks} />;
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-white rounded-lg shadow-sm"
      >
        {/* Loading Progress */}
        {(loading || !initialized) && progress.total > 0 && (
          <div className="p-4 border-b">
            <LoadingProgress
              progress={progress.current}
              total={progress.total}
              label={translations?.loading?.loadingLinks || 'Loading links...'}
            />
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow-sm space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <input
                type="text"
                placeholder={translations?.linkingTable?.filters?.searchPlaceholder || 'Search by name or passport...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            </div>

            {/* Status Filter */}
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">
                {translations?.linkingTable?.filters?.allStatuses || 'All Statuses'}
              </option>
              <option value="approved">
                {statusLabels.approved}
              </option>
              <option value="pending">
                {statusLabels.pending}
              </option>
              <option value="disputed">
                {statusLabels.disputed}
              </option>
              <option value="rejected">
                {statusLabels.rejected}
              </option>
              <option value="waiting_identification">
                {statusLabels.waiting_identification}
              </option>
            </select>

            {/* Country Filter */}
            <select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">
                {translations?.linkingTable?.filters?.allCountries || 'All Countries'}
              </option>
              {countries.map((country) => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>

            {/* Organizer Filter */}
            <select
              value={selectedOrganizer}
              onChange={(e) => setSelectedOrganizer(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">
                {translations?.linkingTable?.filters?.allOrganizers || 'All Organizers'}
              </option>
              {organizers.map((organizer) => (
                <option key={organizer} value={organizer}>{organizer}</option>
              ))}
            </select>
          </div>

          {/* Active Filters */}
          {(searchTerm || selectedStatus || selectedCountry || selectedOrganizer) && (
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-primary-500" />
              <span className="text-sm text-gray-600">
                {translations?.linkingTable?.filters?.activeFilters || 'Active Filters:'}
              </span>
              <div className="flex flex-wrap gap-2">
                {searchTerm && (
                  <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                    {translations?.linkingTable?.filters?.search || 'Search'}: {searchTerm}
                  </span>
                )}
                {selectedStatus && (
                  <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                    {translations?.linkingTable?.filters?.status || 'Status'}:{' '}
                    {statusLabels[selectedStatus] || selectedStatus}
                  </span>
                )}
                {selectedCountry && (
                  <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                    {translations?.linkingTable?.filters?.country || 'Country'}: {selectedCountry}
                  </span>
                )}
                {selectedOrganizer && (
                  <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded flex items-center gap-1">
                    <Building size={12} />
                    <span>{translations?.linkingTable?.filters?.organizer || 'Organizer'}: {selectedOrganizer}</span>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                  {translations?.linkingTable?.tableHeaders?.pilgrim || 'Pilgrim'}
                </th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                  {translations?.linkingTable?.tableHeaders?.guide || 'Guide'}
                </th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                  {translations?.linkingTable?.tableHeaders?.organizer || 'Organizer'}
                </th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                  {translations?.linkingTable?.tableHeaders?.status || 'Status'}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedLinks.map((link) => (
                <LinkingTableRow key={link.id} link={link} />
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className={`p-1 rounded-lg ${
                currentPage === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ChevronRight size={20} />
            </button>

            <span className="text-sm text-gray-700">
              {translations?.pagination?.page || 'Page'} {currentPage}{' '}
              {translations?.pagination?.of || 'of'} {totalPages}
            </span>

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className={`p-1 rounded-lg ${
                currentPage === totalPages
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ChevronLeft size={20} />
            </button>
          </div>

          <div className="text-sm text-gray-500">
            {translations?.pagination?.showing || 'Showing'} {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, filteredLinks.length)} {translations?.pagination?.of || 'of'} {filteredLinks.length}
          </div>
        </div>

        {/* Bottom section with loading indicator or empty state */}
        <div className="p-4 border-t">
          <div className="flex items-center justify-between">
            {loading && (
              <div className="flex items-center gap-2 text-primary-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">
                  {translations?.loading?.loadingData || 'Loading data...'}
                </span>
              </div>
            )}
            <span className="text-sm text-gray-500">
              {translations?.linkingTable?.totalPilgrims || 'Total'}: {filteredLinks.length} {translations?.pagination?.pilgrims || 'pilgrims'}
            </span>
          </div>
        </div>

        {filteredLinks.length === 0 && !loading && initialized && (
          <div className="p-8 text-center text-gray-500">
            {translations?.linkingTable?.noLinks || 'No linked pilgrims currently.'}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};
